// Pure helpers for the time-off heatmap. No React, no side effects — kept this
// way so the month geometry can be unit-tested later if a runner is added.
//
// The heatmap places every time-off entry on a horizontal day-of-month axis
// (each cell = one month, days 1..N laid out left-to-right) and a Doctor×Month
// grid for the visible year. This module owns that geometry plus the shared
// accent palette (rose for holiday, amber for vacation, slate for blocks).

import { DateTime } from "luxon";
import { CalendarRange, PartyPopper, Plane } from "lucide-react";

// Single-letter month strip for compact headers. The slot heatmap uses
// `WEEKDAY_LETTERS` for the same purpose on a different axis.
export const MONTH_LETTERS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

// 3-letter month names for header cells (wider than weekday letters; fits at
// the slots-heatmap's column width).
export const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Long month names for the detail rail header ("in August 2026").
export const MONTH_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Shared visual accent for every time-off type. Lifted from
// day-view-time-off-panel.jsx so the day-view list and the heatmap share colors.
// The `barClass` is new — used by the month-cell horizontal bars.
export function entryAccent(row) {
  if (row?.type === "holiday") {
    return {
      icon: PartyPopper,
      cardClass:
        "border-l-4 border-l-rose-500 bg-rose-50/70 dark:border-l-rose-500/60 dark:bg-rose-950/20",
      iconClass: "text-rose-600 dark:text-rose-300",
      bandHex: "#f43f5e", // rose-500
      barClass: "bg-rose-500/85 dark:bg-rose-500/75",
    };
  }
  if (row?.type === "vacation") {
    return {
      icon: Plane,
      cardClass:
        "border-l-4 border-l-amber-500 bg-amber-50/70 dark:border-l-amber-500/60 dark:bg-amber-950/20",
      iconClass: "text-amber-600 dark:text-amber-300",
      bandHex: "#f59e0b", // amber-500
      barClass: "bg-amber-500/85 dark:bg-amber-500/75",
    };
  }
  return {
    icon: CalendarRange,
    cardClass:
      "border-l-4 border-l-slate-400 bg-slate-50 dark:border-l-slate-500 dark:bg-slate-900/40",
    iconClass: "text-slate-500 dark:text-slate-300",
    bandHex: "#64748b", // slate-500
    barClass: "bg-slate-400/85 dark:bg-slate-500/75",
  };
}

// "Aug 15 – Aug 25, 2026" / "Aug 15, 2026" / "Aug 15 14:00 – 17:30"
// Lifted from day-view-time-off-panel.jsx (kept identical for parity).
export function formatEntryRange(row) {
  if (!row?.startAt?.toFormat) return "";
  if (row.allDay) {
    const lastInclusive = row.endAt
      ? row.endAt.minus({ days: 1 }).startOf("day")
      : row.startAt;
    if (!lastInclusive.isValid || +lastInclusive <= +row.startAt) {
      return row.startAt.toFormat("LLL d, yyyy");
    }
    if (lastInclusive.year === row.startAt.year && lastInclusive.month === row.startAt.month) {
      return `${row.startAt.toFormat("LLL d")} – ${lastInclusive.toFormat("d, yyyy")}`;
    }
    return `${row.startAt.toFormat("LLL d, yyyy")} – ${lastInclusive.toFormat("LLL d, yyyy")}`;
  }
  return `${row.startAt.toFormat("LLL d, HH:mm")} – ${row.endAt.toFormat("HH:mm")}`;
}

// Horizontal placement of an entry inside a single month cell, as percentages
// of the month's day count. Clips to the month boundaries — an entry spanning
// Dec 28 → Jan 5 returns geometry for both months (called once per month).
// Returns null when the entry doesn't intersect the month at all.
//
// `widthPct` has a 4% floor so a single-day all-day entry stays visible even
// in a 31-day month (1/31 = 3.2%, would otherwise render as a hair-thin line).
//
// For `allDay` entries, the upstream API returns `endAt` as the next-day
// midnight; we subtract one day so a 1-day holiday on Aug 15 occupies day-15
// and not day-15+16.
export function monthCellGeometry(entry, monthStart) {
  if (!entry?.startAt?.isValid || !monthStart?.isValid) return null;
  const monthEnd = monthStart.endOf("month");
  const daysInMonth = monthStart.daysInMonth;

  const rawEnd =
    entry.allDay && entry.endAt?.isValid
      ? entry.endAt.minus({ days: 1 })
      : entry.endAt;
  const end = rawEnd?.isValid ? rawEnd : entry.startAt;

  // Clip to month bounds.
  if (end < monthStart || entry.startAt > monthEnd) return null;
  const clipStart = entry.startAt < monthStart ? monthStart : entry.startAt;
  const clipEnd = end > monthEnd ? monthEnd : end;

  const startDay = clipStart.day; // 1..N
  const endDay = clipEnd.day; // 1..N
  const leftPct = ((startDay - 1) / daysInMonth) * 100;
  const widthPct = Math.max(4, ((endDay - startDay + 1) / daysInMonth) * 100);
  return { leftPct, widthPct };
}

// True when the entry intersects the month at all. Cheap predicate used by the
// matrix to pick which entries to hand to a `MonthCell`.
export function entryIntersectsMonth(entry, monthStart) {
  return monthCellGeometry(entry, monthStart) !== null;
}

// Year-bounded range builders for the toolbar.
export function yearRange(year) {
  const start = DateTime.fromObject({ year, month: 1, day: 1 }).startOf("year");
  return { from: start, to: start.endOf("year") };
}

// Month-start Luxon DateTimes for every month of the visible year — used by
// both the matrix header and every cell's geometry call.
export function yearMonths(year) {
  return Array.from({ length: 12 }, (_, idx) =>
    DateTime.fromObject({ year, month: idx + 1, day: 1 }).startOf("month"),
  );
}
